/**
 * useCancelOrder Hook
 *
 * Cancels an order using URQL mutation.
 */

import { useCallback } from "react";
import { useMutation } from "urql";
import { errorTracking } from "@/lib/error-tracking";
import type { Order } from "@/generated/graphql";

// =============================================================================
// MUTATION DOCUMENT
// =============================================================================

const CancelOrderDocument = `
  mutation CancelOrder($orderId: ID!) {
    cancelOrder(orderId: $orderId) {
      id
      status
    }
  }
`;

// =============================================================================
// HOOK
// =============================================================================

interface UseCancelOrderResult {
  cancelOrder: (orderId: string) => Promise<Order>;
  isLoading: boolean;
  error: Error | null;
}

/**
 * Cancel an order.
 *
 * Uses URQL mutation with automatic cache invalidation.
 */
export function useCancelOrder(): UseCancelOrderResult {
  const [result, executeMutation] = useMutation<
    { cancelOrder: Order },
    { orderId: string }
  >(CancelOrderDocument);

  const cancelOrder = useCallback(
    async (orderId: string): Promise<Order> => {
      try {
        const response = await executeMutation({ orderId });

        if (response.error) {
          throw new Error(
            response.error.graphQLErrors[0]?.message ||
              response.error.message ||
              "Failed to cancel order"
          );
        }

        if (!response.data?.cancelOrder) {
          throw new Error("No order returned from server");
        }

        const order = response.data.cancelOrder;

        errorTracking.addBreadcrumb({
          category: "order",
          message: "Order cancelled",
          data: { orderId: order.id, status: order.status },
        });

        return order;
      } catch (err) {
        const cancelError =
          err instanceof Error ? err : new Error("Failed to cancel order");

        errorTracking.captureError(cancelError, {
          tags: { feature: "orders", action: "cancelOrder" },
          extra: { orderId },
        });

        throw cancelError;
      }
    },
    [executeMutation]
  );

  return {
    cancelOrder,
    isLoading: result.fetching,
    error: result.error ?? null,
  };
}
